import React from 'react';
import { Link } from 'react-router-dom';
import { Popup } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { CheckCircle, XCircle, AlertTriangle } from '@untitledui/icons';
import CustomIcon from '../../../../components/icons/CustomIcon';
import { AccountActivityElementModel } from '../types';
import {
  formatActor,
  formatAction,
  formatTarget,
  getOutcomeConfig,
  extractMetadata,
  formatPayload,
} from '../helpers';
import { formatTimeElapsed } from '../../../../utils/time/formatTimeElapsed';
import { i18nKeys } from '../constants';
import PayloadTooltip from './PayloadTooltip';
import styles from './ActivityCard.module.css';

interface ActivityCardProps {
  activity: AccountActivityElementModel;
}

const OutcomeIcon: React.FC<{ outcome: string; color: string }> = ({ outcome, color }) => {
  const size = 16;
  switch (outcome) {
    case 'success':
      return <CheckCircle size={size} color={color} />;
    case 'failed':
      return <XCircle size={size} color={color} />;
    case 'blocked':
      return <AlertTriangle size={size} color={color} />;
    default:
      return <CheckCircle size={size} color={color} />;
  }
};

/**
 * ActivityCard - Display a single audit trail activity entry
 */
const ActivityCard: React.FC<ActivityCardProps> = ({ activity }) => {
  const actor = formatActor(activity);
  // A key actor has no account id, so the usual "#123" popup would read "#null". Show which key it was
  // instead, since that is the equivalent identifier for a machine caller.
  const actorDetail = actor.isAccessKey
    ? `Access key #${activity.accessKeyId}`
    : [`#${actor.accountId}`, actor.fullName].filter(Boolean).join(' ');
  const action = formatAction(activity);
  const target = formatTarget(activity);
  const outcomeConfig = getOutcomeConfig(activity.outcome);
  const metadata = extractMetadata(activity);
  const payloadItems = formatPayload(activity.payload);
  const hasPayload = payloadItems.length > 0;

  // Format time - elapsed with tooltip showing local time
  // createdAt is in UTC format, so we need to tell formatTimeElapsed to parse it as UTC
  const elapsedTime = formatTimeElapsed(activity.createdAt, undefined, { isUtc: true });
  const localTime = activity.createdAtLocal;
  const signInDetailId = `activity-${activity.id}-sign-in`;

  // Build request info for action tooltip
  const requestInfo = metadata.requestMethod && metadata.requestPath
    ? `${metadata.requestMethod} ${metadata.requestPath}`
    : null;

  // Build context info for third row (org/event/campaign)
  const contextItems: { label: string; id: number; value: string }[] = [];
  
  if (activity.targetOrganization) {
    contextItems.push({
      label: 'Org:',
      id: activity.targetOrganization.id,
      value: activity.targetOrganization.urlPath,
    });
  } else if (actor.isAccessKey && activity.organization) {
    // The actor's own organization, shown only when there is no target org to show instead. A list
    // export has no single target, so a key-driven one would otherwise carry no charity at all, and
    // key descriptions are reused across charities so the name alone does not identify the caller.
    contextItems.push({
      label: 'Org:',
      id: activity.organization.id,
      value: activity.organization.urlPath,
    });
  }
  if (activity.targetEvent) {
    contextItems.push({
      label: 'Event:',
      id: activity.targetEvent.id,
      value: activity.targetEvent.urlPath,
    });
  }
  if (activity.targetCampaign) {
    contextItems.push({
      label: 'Campaign:',
      id: activity.targetCampaign.id,
      value: activity.targetCampaign.name,
    });
  }

  return (
    <div className={styles.activityCard}>
      {/* Main Row: Outcome Icon + Summary + Time */}
      <div className={styles.mainRow}>
        <div className={styles.outcomeIcon} style={{ color: outcomeConfig.color }}>
          <OutcomeIcon outcome={activity.outcome} color={outcomeConfig.color} />
        </div>

        <div className={styles.summarySection}>
          {/* Actor */}
          <span className={styles.actor}>
            <Popup
              trigger={<span>{actor.displayName}</span>}
              content={actorDetail}
              position="top center"
              size="small"
              inverted
            />
            {actor.isImpersonated && (
              <Popup
                trigger={
                  <span className={styles.impersonationIcon}>
                    <CustomIcon name="switch-horizontal-01" size={14} />
                  </span>
                }
                content={
                  <div>
                    <strong>{I18n.t(i18nKeys.IMPERSONATED_BY)}</strong>
                    <br />
                    {actor.impersonatorName}
                    {actor.impersonatorEmail && (
                      <>
                        <br />
                        <span className={styles.impersonatorEmail}>{actor.impersonatorEmail}</span>
                      </>
                    )}
                  </div>
                }
                position="top center"
                size="small"
                inverted
              />
            )}
          </span>

          {/* Action */}
          {requestInfo ? (
            <Popup
              trigger={<span className={styles.action}>{action}</span>}
              content={<code className={styles.requestInfo}>{requestInfo}</code>}
              position="top center"
              size="small"
              inverted
            />
          ) : (
            <span className={styles.action}>{action}</span>
          )}

          {/* Target */}
          {target && (
            <>
              {target.link ? (
                <Link to={target.link} className={styles.targetLink}>
                  {target.description}
                </Link>
              ) : (
                <span className={styles.target}>{target.description}</span>
              )}
            </>
          )}

          {/* Outcome Badge */}
          <span
            className={styles.outcomeBadge}
            style={{
              color: outcomeConfig.color,
              backgroundColor: outcomeConfig.backgroundColor,
            }}
          >
            {outcomeConfig.label}
          </span>
        </div>

        {/* Time */}
        <Popup
          trigger={<span className={styles.timeElapsed}>{elapsedTime}</span>}
          content={localTime}
          position="top right"
          size="small"
          inverted
        />
      </div>

      {/* Metadata Row */}
      <div className={styles.metadataRow}>
        {metadata.ipAddress && (
          <Popup
            trigger={
              <span className={styles.metadataItem}>
                <span className={styles.metadataLabel}>IP:</span>
                <span className={styles.metadataValue}>{metadata.ipAddress}</span>
              </span>
            }
            content={
              metadata.userAgent ? (
                <span className={styles.userAgentTooltip}>{metadata.userAgent}</span>
              ) : (
                'Unknown user agent'
              )
            }
            position="top left"
            size="small"
            inverted
          />
        )}

        {/* A row that recorded how its session signed in says so; an older row keeps the
            account-level MFA badge, which only says the account had MFA set up at the time. */}
        {metadata.signIn ? (
          <>
            <Popup
              trigger={
                <span
                  className={styles.metadataItem}
                  tabIndex={0}
                  aria-describedby={signInDetailId}
                >
                  <span className={styles.metadataBadge}>
                    {metadata.signIn.badge}
                  </span>
                </span>
              }
              content={metadata.signIn.detail}
              on={['hover', 'focus']}
              position="top left"
              size="small"
              inverted
            />
            {/* Hidden nodes still count for aria-describedby, so screen readers get the detail */}
            <span id={signInDetailId} hidden>
              {metadata.signIn.detail}
            </span>
          </>
        ) : (
          metadata.mfaUsed && (
            <span className={styles.metadataItem}>
              <span className={styles.metadataBadge}>
                {I18n.t(i18nKeys.MFA_VERIFIED)}
              </span>
            </span>
          )
        )}

        {metadata.activityType && (
          <span className={styles.metadataItem}>
            <span className={styles.metadataValue}>{metadata.activityType}</span>
            {metadata.showSubtype && metadata.activitySubtype && (
              <span className={styles.metadataSubtype}>/{metadata.activitySubtype}</span>
            )}
          </span>
        )}

        {metadata.adminContextType && metadata.adminRole && (
          <span className={styles.metadataItem}>
            <span className={styles.metadataValue}>{metadata.adminContextType} {metadata.adminRole}</span>
          </span>
        )}

        {/* Suppressed when the key is itself the actor, since the name above already says it. For a
            human session issued through a bootstrap key this still earns its place: it names the route
            in, which the actor line does not. */}
        {metadata.accessKeyDescription && !actor.isAccessKey && (
          <span className={styles.metadataItem}>
            <span className={styles.metadataLabel}>{I18n.t(i18nKeys.ACCESS_KEY)}:</span>
            <span className={styles.metadataValue}>{metadata.accessKeyDescription}</span>
          </span>
        )}

        {hasPayload && (
          <PayloadTooltip
            payload={payloadItems}
            trigger={
              <span className={styles.additionalDataLink}>
                {I18n.t(i18nKeys.ADDITIONAL_DATA)}
              </span>
            }
          />
        )}
      </div>

      {/* Context Row - Organization/Event/Campaign info */}
      {contextItems.length > 0 && (
        <div className={styles.contextRow}>
          {contextItems.map((item) => (
            <span key={`${item.label}-${item.id}`} className={styles.contextItem}>
              <span className={styles.contextLabel}>{item.label}</span>
              <span className={styles.contextValue}>#{item.id} {item.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
